import { useEffect, useRef, useCallback, useState } from 'react';

const IDLE_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

const DEFAULT_IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

/** Allow overriding timeout for E2E tests via window global */
function getIdleTimeoutMs(): number {
  try {
    const override = (window as unknown as Record<string, unknown>).__TEST_IDLE_TIMEOUT_MS;
    if (typeof override === 'number' && override > 0) {
      return override;
    }
  } catch {
    /* SSR guard */
  }
  return DEFAULT_IDLE_TIMEOUT_MS;
}
const STORAGE_KEY = 'lastActivityTimestamp';

/**
 * Shared cookie name used across fedix.ai subdomains (CRM + MyLedger)
 * to sync user activity. This ensures that working on MyLedger keeps
 * the CRM session alive and vice versa.
 */
const ACTIVITY_COOKIE_NAME = 'fedix_last_activity';

/** Write a cross-subdomain activity timestamp cookie on .fedix.ai */
function setActivityCookie() {
  const isLocalhost =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const domain = isLocalhost ? '' : '; Domain=.fedix.ai';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${ACTIVITY_COOKIE_NAME}=${Date.now()}; Path=/${domain}${secure}; SameSite=Lax; Max-Age=86400`;
}

/** Read the cross-subdomain activity timestamp cookie */
function getActivityCookieTime(): number {
  const match = document.cookie.match(new RegExp(`(?:^|; )${ACTIVITY_COOKIE_NAME}=([^;]*)`));
  return match ? Number(match[1]) : 0;
}

/**
 * Monitors user activity and triggers a lock screen after 30 minutes of
 * inactivity.
 *
 * Returns `{ locked, unlock }` — the consuming component renders a lock
 * overlay when `locked` is true and calls `unlock()` when the user
 * dismisses it.
 *
 * Syncs across tabs via localStorage (same-origin) and across subdomains
 * (CRM ↔ MyLedger) via a shared `fedix_last_activity` cookie on .fedix.ai.
 * When the idle timer fires, it checks the cookie first — if another subdomain
 * recorded recent activity, the timer resets instead of locking.
 */
export function useIdleTimeout(enabled: boolean) {
  const [locked, setLocked] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Persist timestamp so other tabs can see the latest activity
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // localStorage may be unavailable (private browsing quota exceeded, etc.)
    }

    // Also update the shared cross-subdomain cookie
    setActivityCookie();

    timerRef.current = setTimeout(() => {
      // Before locking, check the shared cookie.
      // Another subdomain (e.g. MyLedger) may have recorded recent activity.
      const lastCrossActivity = getActivityCookieTime();
      const timeout = getIdleTimeoutMs();
      if (lastCrossActivity > 0 && Date.now() - lastCrossActivity < timeout) {
        // Another subdomain is still active — reset instead of locking
        resetTimer();
        return;
      }
      setLocked(true);
    }, getIdleTimeoutMs());
  }, []);

  const unlock = useCallback(() => {
    setLocked(false);
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Always start fresh on mount — the user just logged in or opened a new tab.
    resetTimer();

    // Listen for user activity events
    const handler = () => {
      // Only reset timer when not locked — activity on the blurred page
      // behind the lock overlay should not dismiss the lock.
      if (!timerRef.current) {
        return;
      }
      resetTimer();
    };
    for (const event of IDLE_EVENTS) {
      window.addEventListener(event, handler, { passive: true });
    }

    // Listen for activity in other tabs via localStorage changes
    const storageHandler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        // Another tab recorded activity — reset our timer too
        resetTimer();
      }
    };
    window.addEventListener('storage', storageHandler);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      for (const event of IDLE_EVENTS) {
        window.removeEventListener(event, handler);
      }
      window.removeEventListener('storage', storageHandler);
    };
  }, [enabled, resetTimer]);

  return { locked, unlock };
}
